import {
  createClient,
  type InferModel,
  type LovelicaApiClient,
  type RealtimeServerEventPayloadMap,
} from "@neontree/lovelica-api-client";
import { getAuthorizationHeaders } from "services/coreapi";

/**
 * truelovelica's slice of the platform API (lovelica-core): the safe bot
 * catalogue, the user's copies of those bots, chats and messages. Replies
 * arrive over the realtime socket (`stores/ChatStore`). Authenticates with
 * the core session's bearer token, like the other apps.
 *
 * Sends `x-nt-project: truelovelica` on every call: lovelica-core then
 * serves this app as safe-only, so even a Lovelica (adult) account signed in
 * here never reaches adult bots or chats (the "hard stop", lovelica-core
 * `src/server/safeOnlyClient.ts`). It also asks for `catalogue=sfw`.
 */
export const PLATFORM_API_URL: string =
  import.meta.env.VITE_PLATFORM_API_URL || "https://platformapi.truelovelica.com";

// `parseDate: false`: Eden would turn any date-looking string (a message
// that says "12/05/2024") into a Date. Timestamps are read with `toMillis`.
export const platformapiClient = createClient(PLATFORM_API_URL, {
  headers: () => ({
    ...getAuthorizationHeaders(),
    "x-nt-project": "truelovelica",
  }),
  parseDate: false,
});

const client = platformapiClient;

export type BotTemplate = InferModel<
  LovelicaApiClient["avatars"]["list"]["get"]
>["items"][number];
export type UserBot = InferModel<
  LovelicaApiClient["userAvatars"]["list"]["get"]
>[number];
export type ChatSummary = InferModel<
  LovelicaApiClient["chat"]["listChats"]["get"]
>[number];
export type ChatMessageDTO =
  RealtimeServerEventPayloadMap["chat.message.created"]["message"];

/** An error response from the API, with its HTTP status (0 = no answer). */
export class PlatformAPIError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

interface TreatyResult<T> {
  data: T | null;
  error: { status: number; value: unknown } | null;
  response?: Response;
}

async function request<T>(call: () => Promise<TreatyResult<T>>): Promise<T> {
  const res = await call();
  if (res.error) {
    const value = res.error.value as { message?: unknown } | null;
    const message =
      typeof value?.message === "string"
        ? value.message
        : `Request failed with status ${res.error.status}`;
    throw new PlatformAPIError(message, res.response ? res.error.status : 0);
  }
  if (res.data === null || res.data === undefined) {
    throw new PlatformAPIError("Response had no data", 0);
  }
  return res.data;
}

/** Only the safe catalogue (lovelica-core sends it anyway, see above). */
const SFW = { catalogue: "sfw" } as const;

/** A wire timestamp (ISO string, or Date if something revived it) → epoch ms. */
export function toMillis(value: string | Date): number {
  return value instanceof Date ? value.getTime() : Date.parse(value);
}

export const platformapi = {
  /** `GET /avatars/list?catalogue=sfw` — the 42-odd companions fit one page. */
  listTemplates: () =>
    request(() =>
      client.avatars.list.get({ query: { page: 1, limit: 100, ...SFW } }),
    ).then((r) => r.items),

  /** `GET /userAvatars/list?catalogue=sfw` — the user's copies of the bots. */
  listUserBots: () =>
    request(() => client.userAvatars.list.get({ query: SFW })),

  /** `POST /userAvatars/create` — idempotent (upsert on user × template). */
  createUserBot: (templateId: string) =>
    request(() => client.userAvatars.create.post({ templateId })),

  /** `GET /chat/listChats?catalogue=sfw` */
  listChats: () => request(() => client.chat.listChats.get({ query: SFW })),

  /** `POST /chat/startChat` — idempotent; a new chat gets the greeting. */
  startChat: (botId: string) =>
    request(() => client.chat.startChat.post({ botId })),

  /** `GET /chat/getMessages` — newest first. */
  getMessages: (chatId: string, limit = 50) =>
    request(() => client.chat.getMessages.get({ query: { chatId, limit } })),

  /** `POST /chat/sendMessage` — returns the user's message; the reply comes over realtime. */
  sendMessage: (input: { id: string; chatId: string; content: string }) =>
    request(() => client.chat.sendMessage.post(input)).then((r) => r.message),

  /** `GET /chat/getStatuses` — e.g. the bot is typing. */
  getStatuses: (chatId: string) =>
    request(() => client.chat.getStatuses.get({ query: { chatId } })).then(
      (r) => r.statuses,
    ),
};
